import React, { useEffect, useMemo, useCallback, useState } from "react";
import {Table,Select,Input,Pagination,Tag,Row,Col,Tabs,Spin,Alert,Space,Typography,Card,} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reducer, sliceKey, actions } from "./slice";
import { parameterAnalyticsDetailedSaga } from "./saga";
import {
  selectTableData,
  selectLoading,
  selectError,
  selectPagination,
  selectFilters,
} from "./selectors";

const { Option } = Select;
const { TabPane } = Tabs;
const { Title, Text } = Typography;

const DetailedParameterAnalytics = ({ filters = {} }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  // Local state for client-side filtering (parameter filter only)
  const [selectedParameter, setSelectedParameter] = useState("All");

  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: parameterAnalyticsDetailedSaga });

  const loading = useSelector(selectLoading);
  const data = useSelector(selectTableData);
  const error = useSelector(selectError);
  const pagination = useSelector(selectPagination);
  const appliedFilters = useSelector(selectFilters);

  // Current Active Tab
  const currentView = appliedFilters.parameterType === "All" ? "Overall" : appliedFilters.parameterType;

  // Memoize filters to prevent infinite renders
  const memoizedFilters = useMemo(
    () => ({
      companyIds: filters.companyIds || [],
      projectCodeIds: filters.projectCodeIds || [],
      selectedCommodities: filters.selectedCommodities || [],
      selectedTestingTypes: filters.selectedTestingTypes || [],
      parameterRequest: filters.parameterRequest || "overall",
    }),
    [
      filters.companyIds,
      filters.projectCodeIds,
      filters.selectedCommodities,
      filters.selectedTestingTypes,
      filters.parameterRequest,
    ]
  );

  // Extract unique parameters from API data for dropdown
  const availableParameters = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const uniqueParameters = [...new Set(data.map(item => item.parameter))];
    return uniqueParameters.sort().map(param => ({
      value: param,
      label: param
    }));
  }, [data]);

  // Create stable callback for navigation
  const handleBackClick = useCallback(() => {
    history.push("/bo/dashboard", { filters: memoizedFilters });
  }, [history, memoizedFilters]);

  // Handle filter changes that require API calls
  const handleViewChange = useCallback(
    (view) => {
      const parameterType = view === "Overall" ? "All" : view;
      const newFilters = { ...appliedFilters, parameterType };
      dispatch(actions.setFilters(newFilters));
      
      // Reset client-side parameter filter when changing view
      setSelectedParameter("All");
      
      // Fetch new data with updated filters and reset to page 1
      dispatch(
        actions.fetchDetailed({
          filtersString: JSON.stringify(memoizedFilters),
          pagination: { currentPage: 1, pageSize: pagination.pageSize },
          filters: newFilters,
        })
      );
    },
    [dispatch, memoizedFilters, appliedFilters, pagination.pageSize]
  );

  const handleSortChange = useCallback(
    (sortBy) => {
      const newFilters = { ...appliedFilters, sortBy };
      dispatch(actions.setFilters(newFilters));
      
      // Fetch new data with updated sorting and reset to page 1
      dispatch(
        actions.fetchDetailed({
          filtersString: JSON.stringify(memoizedFilters),
          pagination: { currentPage: 1, pageSize: pagination.pageSize },
          filters: newFilters,
        })
      );
    },
    [dispatch, memoizedFilters, appliedFilters, pagination.pageSize]
  );

  // Client-side parameter filter change (no API call)
  const handleParameterChange = useCallback(
    (parameter) => {
      setSelectedParameter(parameter || "All");
    },
    []
  );

  // Handle page size change - triggers API call
  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      dispatch(actions.setPageSize(newPageSize));
      
      // Fetch new data with updated page size
      dispatch(
        actions.fetchDetailed({
          filtersString: JSON.stringify(memoizedFilters),
          pagination: { currentPage: 1, pageSize: newPageSize },
          filters: appliedFilters,
        })
      );
    },
    [dispatch, memoizedFilters, appliedFilters]
  );

  // Handle pagination change - triggers API call
  const handleTableChange = useCallback(
    (page, size) => {
      const newPageSize = size || pagination.pageSize;
      
      // Update pagination in state
      dispatch(actions.setPagination({ currentPage: page, pageSize: newPageSize }));
      
      // Fetch new data for the selected page
      dispatch(
        actions.fetchDetailed({
          filtersString: JSON.stringify(memoizedFilters),
          pagination: { currentPage: page, pageSize: newPageSize },
          filters: appliedFilters,
        })
      );
    },
    [dispatch, memoizedFilters, appliedFilters, pagination.pageSize]
  );

  useEffect(() => {
    dispatch(
      actions.fetchDetailed({
        filtersString: JSON.stringify(memoizedFilters),
        pagination: {
          currentPage: 1, 
          pageSize: pagination.pageSize, 
        },
        filters: appliedFilters,
      })
    );
    
    setSelectedParameter("All");
  }, [dispatch, memoizedFilters, appliedFilters.parameterType, appliedFilters.sortBy]);

  // Client-side filtering for parameter only
  const filteredTableData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    let processedData = data.map((item, idx) => ({
      key: `${item.parameter}-${item.testType}-${item.commodity}-${idx}`,
      id: `${item.parameter}-${item.testType}-${item.commodity}-${idx}`,
      testType: item.testType,
      parameter: item.parameter,
      parameterType: item.parameterType,
      commodity: item.commodity,
      total: item.total,
      safe: item.safe,
      unsafe: item.unsafe,
      standard: item.standard,
      subStandard: item.subStandard,
      compliance: item.compliancePercentage,
      unit: item.unit,
      safetyCompliance: item.safetyCompliance,
      qualityCompliance: item.qualityCompliance,
      overallCompliance: item.overallCompliance,
      totalChecks:
        currentView === "Safety"
          ? item.safe + item.unsafe
          : currentView === "Quality"
          ? item.standard + item.subStandard
          : item.safe + item.unsafe + item.standard + item.subStandard,
    }));

    // Client-side parameter filtering
    if (selectedParameter && selectedParameter !== "All") {
      processedData = processedData.filter(item => 
        item.parameter === selectedParameter
      );
    }

    return processedData;
  }, [data, selectedParameter]);

  const columns = [
    {
      title: "Test Type",
      dataIndex: "testType",
      key: "testType",
      sorter: (a, b) => a.testType.localeCompare(b.testType),
    },
    {
      title: "Parameter",
      dataIndex: "parameter",
      key: "parameter",
      sorter: (a, b) => a.parameter.localeCompare(b.parameter),
    },
    {
      title: "Parameter Type",
      dataIndex: "parameterType",
      key: "parameterType",
      sorter: (a, b) => a.parameterType.localeCompare(b.parameterType),
      render: (type) =>
        type === "Safety" ? (
          <Tag color="red">{type}</Tag>
        ) : (
          <Tag color="blue">{type}</Tag>
        ),
    },
    {
      title: "Commodity",
      dataIndex: "commodity",
      key: "commodity",
      sorter: (a, b) => a.commodity.localeCompare(b.commodity),
    },
    {
      title: "Total Orders",
      dataIndex: "total",
      key: "total",
      sorter: (a, b) => a.total - b.total,
    },
    {
      title: "Total Checks",
      dataIndex: "totalChecks",
      key: "totalChecks",
      sorter: (a, b) => a.totalChecks - b.totalChecks,
    },
    ...(currentView !== "Quality"
      ? [
          {
            title: "Safe",
            dataIndex: "safe",
            key: "safe",
            sorter: (a, b) => a.safe - b.safe,
          },
          {
            title: "Unsafe",
            dataIndex: "unsafe",
            key: "unsafe",
            sorter: (a, b) => a.unsafe - b.unsafe,
          },
        ]
      : []),
    ...(currentView !== "Safety"
      ? [
          {
            title: "Standard",
            dataIndex: "standard",
            key: "standard",
            sorter: (a, b) => a.standard - b.standard,
          },
          {
            title: "Sub-Standard",
            dataIndex: "subStandard",
            key: "subStandard",
            sorter: (a, b) => a.subStandard - b.subStandard,
          },
        ]
      : []),
    {
      title: "Compliance %",
      dataIndex: "compliance",
      key: "compliance",
      sorter: (a, b) => a.compliance - b.compliance,
      render: (val) => (
        <span
          style={{
            fontWeight: 600,
            color: val > 95 ? "#52c41a" : val > 90 ? "#faad14" : "#f5222d",
          }}
        >
          {val?.toFixed(1)}%
        </span>
      ),
    },
  ];

  const paginationConfig = {
    current: pagination.currentPage,
    pageSize: pagination.pageSize,
    total: pagination.totalCount,
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} entries`,
    pageSizeOptions: ["10", "30", "50", "100"],
    onChange: handleTableChange,
    onShowSizeChange: handleTableChange,
    style: { marginTop: 16 },
  };


  return (
    <div>
      <Card
        style={{ marginTop: 24 }}
        bodyStyle={{ padding: 0, backgroundColor: "#fff" }}
      >
        <Row align="middle" style={{ padding: "16px 24px" }}>
          <Col>
            <ArrowLeftOutlined
              style={{
                fontSize: 18,
                color: "#000",
                marginRight: 12,
                cursor: "pointer",
              }}
              onClick={handleBackClick}
            />
          </Col>
          <Col flex="auto">
            <Title level={4} style={{ color: "#000", margin: 0 }}>
              Detailed View – Parameter-Wise Compliance
            </Title>
          </Col>
        </Row>

        {/* Main Content */}
        <div style={{ padding: 24, backgroundColor: "#fcfafaff" }}>
          {error && (
            <Alert
              message="Error loading data"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Header Tabs */}
          <Row
            justify="space-between"
            align="middle"
            style={{ color: "#0000", marginBottom: 20 }}
          >
            <Col>
              <Tabs
                activeKey={currentView}
                onChange={handleViewChange}
                type="card"
                size="small"
                moreIcon={null}
              >
                <TabPane tab="Overall" key="Overall" />
                <TabPane tab="Safety" key="Safety" />
                <TabPane tab="Quality" key="Quality" />
              </Tabs>
            </Col>
          </Row>

          {/* Filters */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: "100%" }}
                placeholder="Filter by parameter"
                allowClear
                showSearch
                value={
                  selectedParameter === "All" ? undefined : selectedParameter
                }
                onChange={handleParameterChange}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {availableParameters.map((param) => (
                  <Option key={param.value} value={param.value}>
                    {param.label}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: "100%" }}
                value={appliedFilters.sortBy}
                onChange={handleSortChange}
              >
                <Option value="compliance">Sort: Compliance %</Option>
                {currentView !== "Quality" && (
                  <>
                    <Option value="safe">Sort: Safe</Option>
                    <Option value="unsafe">Sort: Unsafe</Option>
                  </>
                )}
                {currentView !== "Safety" && (
                  <>
                    <Option value="standard">Sort: Standard</Option>
                    <Option value="subStandard">Sort: Sub-Standard</Option>
                  </>
                )}
                <Option value="total">Sort: Total</Option>
                <Option value="parameter">Sort: Parameter</Option>
                <Option value="testType">Sort: Test Type</Option>
                <Option value="commodity">Sort: Commodity</Option>
                {/* <Option value="totalChecks">Sort: Total Checks</Option> */}
              </Select>
            </Col>
          </Row>

          {/* Summary Info */}
          {!loading && (
            <div style={{ marginBottom: 16, color: "#666" }}>
              <Text>
                Showing {filteredTableData.length} of {pagination.totalCount}{" "}
                entries
                {selectedParameter &&
                  selectedParameter !== "All" &&
                  ` (filtered by parameter: ${selectedParameter})`}
                {memoizedFilters.selectedCommodities?.length > 0 &&
                  ` (filtered by ${
                    memoizedFilters.selectedCommodities.length
                  } commodit${
                    memoizedFilters.selectedCommodities.length > 1 ? "ies" : "y"
                  })`}
                {memoizedFilters.selectedTestingTypes?.length > 0 &&
                  ` (filtered by ${
                    memoizedFilters.selectedTestingTypes.length
                  } test type${
                    memoizedFilters.selectedTestingTypes.length > 1 ? "s" : ""
                  })`}
              </Text>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                Loading parameter compliance data...
              </div>
            </div>
          ) : (
            <div>
              <Table
                columns={columns}
                dataSource={filteredTableData}
                pagination={paginationConfig}
                rowKey="id"
                bordered
                scroll={{ x: true }}
                locale={{ emptyText: "No parameter data available" }}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DetailedParameterAnalytics;